import {
  AddRounded,
  BookmarkOutlined,
  BusinessRounded,
  CloseRounded,
  EventRounded,
  FlashOnRounded,
  ForumRounded,
  HomeRounded,
  LocalActivityRounded,
  MoreHorizRounded,
  PeopleAltRounded,
  SchoolRounded,
  ViewCarouselRounded,
  WifiTetheringRounded
} from '@mui/icons-material'
import { AppBar, Box, Button, ButtonGroup, Drawer, Fab, Grid, Typography, styled } from '@mui/material'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { IRootState } from '../../../redux/types'
import { isAdmin, isCommercial, isManager, isSuperAdmin, isTrainingManager } from '../../backoffice/users/utils/RolesHelper'
import HiddenDesktop from '../styles/HiddenDesktop'

export const CustomButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: 8,
  paddingBottom: 10,
  textTransform: 'none',

  '& .MuiButton-startIcon': {
    margin: 0,
    display: 'flex',
    justifyContent: 'center',

    '& svg': {
      color: theme.palette.grey[400],
      fontSize: '24px !important'
    },
    '&.active svg': {
      color: theme.palette.primary.main
    }
  }
}))

const CustomButtonLink = styled(Button)(({ theme }) => ({
  '&.MuiButton-text': {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 8,
    minWidth: 80,
    minHeight: 80,
    padding: '0px !important',
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.grey[300],
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.16)'
  },
  '& .MuiButton-startIcon': {
    margin: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& svg': {
      fontSize: '24px !important'
    }
  }
}))

const StyledButtonGroup = styled(ButtonGroup)(({ theme }) => ({
  borderRadius: 16,
  backgroundColor: theme.palette.grey[800],
  boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.16)',
  width: 'inherit',
  [theme.breakpoints.between('sm', 'md')]: {
    width: 0
  },
  grouped: {
    minWidth: '25%',
    maxHeight: 71,
    padding: 0,
    border: '0px !important'
  }
}))

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
function AdminMobileMenu({ action }: any) {
  const navigate = useNavigate()
  const { t } = useTranslation(['global'])

  const { user } = useSelector((state: IRootState) => state.auth)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(anchorEl)

  const handleProfileMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const menuId = 'primary-search-account-menu'

  return (
    <HiddenDesktop>
      <AppBar
        sx={{
          top: 'auto',
          bottom: 0,
          padding: 1,
          backgroundColor: 'transparent',
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          gap: '5px',
          alignItems: 'center'
        }}
        position="fixed"
      >
        <StyledButtonGroup>
          <CustomButton
            fullWidth
            startIcon={<HomeRounded color={window.location.pathname === '/escritorio' ? 'secondary' : 'primary'} />}
            onClick={() => navigate('/escritorio')}
          >
            <Typography variant="caption" color={window.location.pathname === '/escritorio' ? 'secondary' : '#8F99A3'}>
              Inicio
            </Typography>
          </CustomButton>
          {isSuperAdmin(user.role_id) && (
            <CustomButton
              fullWidth
              startIcon={<SchoolRounded color={window.location.pathname === '/admin/cursos' ? 'secondary' : 'primary'} />}
              onClick={() => navigate('/admin/cursos')}
            >
              <Typography variant="caption" color={window.location.pathname === '/admin/cursos' ? 'secondary' : '#8F99A3'}>
                {t('academy')}
              </Typography>
            </CustomButton>
          )}
          {!isCommercial(user.role_id) && !isManager(user.role_id) && (
            <CustomButton
              fullWidth
              startIcon={<SchoolRounded color={window.location.pathname === '/admin/cursos' ? 'secondary' : 'primary'} />}
              onClick={() => navigate('/admin/cursos')}
            >
              <Typography variant="caption" color={window.location.pathname === '/admin/cursos' ? 'secondary' : '#8F99A3'}>
                {t('academy')}
              </Typography>
            </CustomButton>
          )}
          {!isCommercial(user.role_id) && !isManager(user.role_id) && (
            <CustomButton
              fullWidth
              startIcon={<BookmarkOutlined color={window.location.pathname === '/admin/biblioteca' ? 'secondary' : 'primary'} />}
              onClick={() => navigate('/admin/biblioteca')}
            >
              <Typography variant="caption" color={window.location.pathname === '/admin/biblioteca' ? 'secondary' : '#8F99A3'}>
                {t('admin.library.library')}
              </Typography>
            </CustomButton>
          )}
          <CustomButton
            fullWidth
            startIcon={
              <MoreHorizRounded
                color={['/escritorio', '/admin/cursos', '/admin/biblioteca'].includes(window.location.pathname) ? 'primary' : 'secondary'}
              />
            }
            onClick={handleProfileMenuOpen}
          >
            <Typography
              variant="caption"
              color={['/escritorio', '/admin/cursos', '/admin/biblioteca'].includes(window.location.pathname) ? '#8F99A3' : 'secondary'}
            >
              {t('see-more')}
            </Typography>
          </CustomButton>
        </StyledButtonGroup>

        <Fab
          sx={{
            height: 56,
            width: 56,
            minWidth: 56,
            backgroundColor: 'secondary.main',
            boxShadow: '0px 6px 24px rgba(252, 62, 111, 0.2)'
          }}
          onClick={action}
        >
          <AddRounded htmlColor="white" aria-label="add" />
        </Fab>
      </AppBar>

      <Box display="flex">
        {isMenuOpen && (
          <HiddenDesktop>
            <Drawer
              variant="temporary"
              anchor="bottom"
              open={isMenuOpen}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
              ModalProps={{ keepMounted: true }}
              PaperProps={{
                sx: {
                  paddingBottom: 12,
                  top: 'auto',
                  bottom: 60,
                  padding: 1,
                  backgroundColor: 'transparent',
                  boxShadow: 'none'
                }
              }}
            >
              <Grid
                sx={theme => ({
                  width: 'inherit',
                  paddingRight: 7,
                  [theme.breakpoints.between('sm', 'md')]: {
                    width: 600,
                    margin: '0 auto'
                  }
                })}
                container
                justifyContent="flex-end"
                spacing={4}
              >
                <Grid item xs={4}>
                  <CustomButtonLink
                    startIcon={<PeopleAltRounded color={window.location.pathname === '/admin/usuarios' ? 'secondary' : 'primary'} />}
                    onClick={() => {
                      navigate('/admin/usuarios')
                      handleMenuClose()
                    }}
                  >
                    <Typography variant="caption" color={window.location.pathname === '/admin/usuarios' ? 'secondary' : '#8F99A3'}>
                      {t('admin.people.people')}
                    </Typography>
                  </CustomButtonLink>
                </Grid>
                {!isCommercial(user.role_id) && !isManager(user.role_id) && (
                  <>
                    <Grid item xs={4}>
                      <CustomButtonLink
                        fullWidth
                        startIcon={<LocalActivityRounded color={window.location.pathname === '/admin/ofertas' ? 'secondary' : 'primary'} />}
                        onClick={() => {
                          navigate('/admin/ofertas')
                          handleMenuClose()
                        }}
                      >
                        <Typography variant="caption" color={window.location.pathname === '/admin/ofertas' ? 'secondary' : '#8F99A3'}>
                          {t('offers')}
                        </Typography>
                      </CustomButtonLink>
                    </Grid>
                    <Grid item xs={4}>
                      <CustomButtonLink
                        fullWidth
                        startIcon={<EventRounded color={window.location.pathname === '/admin/events' ? 'secondary' : 'primary'} />}
                        onClick={() => {
                          navigate('/admin/events')
                          handleMenuClose()
                        }}
                      >
                        <Typography variant="caption" color={window.location.pathname === '/admin/events' ? 'secondary' : '#8F99A3'}>
                          {t('events')}
                        </Typography>
                      </CustomButtonLink>
                    </Grid>
                    <Grid item xs={4}>
                      <CustomButtonLink
                        fullWidth
                        startIcon={<ForumRounded color={window.location.pathname === '/admin/noticias' ? 'secondary' : 'primary'} />}
                        onClick={() => {
                          navigate('/admin/noticias')
                          handleMenuClose()
                        }}
                      >
                        <Typography variant="caption" color={window.location.pathname === '/admin/noticias' ? 'secondary' : '#8F99A3'}>
                          {t('news')}
                        </Typography>
                      </CustomButtonLink>
                    </Grid>
                    <Grid item xs={4}>
                      <CustomButtonLink
                        fullWidth
                        startIcon={<FlashOnRounded color={window.location.pathname === '/admin/novedades' ? 'secondary' : 'primary'} />}
                        onClick={() => {
                          navigate('/admin/novedades')
                          handleMenuClose()
                        }}
                      >
                        <Typography variant="caption" color={window.location.pathname === '/admin/novedades' ? 'secondary' : '#8F99A3'}>
                          {t('updates')}
                        </Typography>
                      </CustomButtonLink>
                    </Grid>
                    <Grid item xs={4}>
                      <CustomButtonLink
                        fullWidth
                        startIcon={<ViewCarouselRounded color={window.location.pathname === '/admin/sliders' ? 'secondary' : 'primary'} />}
                        onClick={() => {
                          navigate('/admin/sliders')
                          handleMenuClose()
                        }}
                      >
                        <Typography variant="caption" color={window.location.pathname === '/admin/sliders' ? 'secondary' : '#8F99A3'}>
                          {t('slider')}
                        </Typography>
                      </CustomButtonLink>
                    </Grid>
                  </>
                )}
                {isSuperAdmin(user.role_id) && (
                  <>
                    {/* Biblioteca */}
                    <Grid item xs={4}>
                      <CustomButtonLink
                        startIcon={<BookmarkOutlined color={window.location.pathname === '/admin/biblioteca' ? 'secondary' : 'primary'} />}
                        onClick={() => navigate('/admin/biblioteca')}
                      >
                        <Typography variant="caption" color={window.location.pathname === '/admin/biblioteca' ? 'secondary' : '#8F99A3'}>
                          {t('admin.library.library')}
                        </Typography>
                      </CustomButtonLink>
                    </Grid>

                    <Grid item xs={4}>
                      <CustomButtonLink
                        startIcon={<LocalActivityRounded color={window.location.pathname === '/admin/ofertas' ? 'secondary' : 'primary'} />}
                        onClick={() => {
                          navigate('/admin/ofertas')
                          handleMenuClose()
                        }}
                      >
                        <Typography variant="caption" color={window.location.pathname === '/admin/ofertas' ? 'secondary' : '#8F99A3'}>
                          {t('offers')}
                        </Typography>
                      </CustomButtonLink>
                    </Grid>

                    <Grid item xs={4}>
                      <CustomButtonLink
                        startIcon={<EventRounded color={window.location.pathname === '/admin/events' ? 'secondary' : 'primary'} />}
                        onClick={() => {
                          navigate('/admin/events')
                          handleMenuClose()
                        }}
                      >
                        <Typography variant="caption" color={window.location.pathname === '/admin/events' ? 'secondary' : '#8F99A3'}>
                          {t('events')}
                        </Typography>
                      </CustomButtonLink>
                    </Grid>

                    <Grid item xs={4}>
                      <CustomButtonLink
                        startIcon={<ForumRounded color={window.location.pathname === '/admin/noticias' ? 'secondary' : 'primary'} />}
                        onClick={() => {
                          navigate('/admin/noticias')
                          handleMenuClose()
                        }}
                      >
                        <Typography variant="caption" color={window.location.pathname === '/admin/noticias' ? 'secondary' : '#8F99A3'}>
                          {t('news')}
                        </Typography>
                      </CustomButtonLink>
                    </Grid>

                    <Grid item xs={4}>
                      <CustomButtonLink
                        startIcon={<FlashOnRounded color={window.location.pathname === '/admin/novedades' ? 'secondary' : 'primary'} />}
                        onClick={() => {
                          navigate('/admin/novedades')
                          handleMenuClose()
                        }}
                      >
                        <Typography variant="caption" color={window.location.pathname === '/admin/novedades' ? 'secondary' : '#8F99A3'}>
                          {t('updates')}
                        </Typography>
                      </CustomButtonLink>
                    </Grid>

                    <Grid item xs={4}>
                      <CustomButtonLink
                        startIcon={<ViewCarouselRounded color={window.location.pathname === '/admin/sliders' ? 'secondary' : 'primary'} />}
                        onClick={() => {
                          navigate('/admin/sliders')
                          handleMenuClose()
                        }}
                      >
                        <Typography variant="caption" color={window.location.pathname === '/admin/sliders' ? 'secondary' : '#8F99A3'}>
                          {t('slider')}
                        </Typography>
                      </CustomButtonLink>
                    </Grid>
                  </>
                )}
                {isAdmin(user.role_id) ||
                  (isTrainingManager(user.role_id) && (
                    <Grid item xs={4}>
                      <CustomButtonLink
                        startIcon={
                          <WifiTetheringRounded color={window.location.pathname === '/admin/seminarios' ? 'secondary' : 'primary'} />
                        }
                        onClick={() => {
                          navigate('/admin/seminarios')
                          handleMenuClose()
                        }}
                      >
                        <Typography variant="caption" color={window.location.pathname === '/admin/seminarios' ? 'secondary' : '#8F99A3'}>
                          {t('seminars')}
                        </Typography>
                      </CustomButtonLink>
                    </Grid>
                  ))}
                {user.role_id === 1 && (
                  <Grid item xs={4}>
                    <CustomButtonLink
                      startIcon={<BusinessRounded color={window.location.pathname === '/admin/marcas' ? 'secondary' : 'primary'} />}
                      onClick={() => {
                        navigate('/admin/marcas')
                        handleMenuClose()
                      }}
                    >
                      <Typography variant="caption" color={window.location.pathname === '/admin/marcas' ? 'secondary' : '#8F99A3'}>
                        {t('brands')}
                      </Typography>
                    </CustomButtonLink>
                  </Grid>
                )}
                <Grid item xs={4}>
                  <CustomButtonLink startIcon={<CloseRounded />} aria-controls={menuId} aria-haspopup="true" onClick={handleMenuClose}>
                    <Typography variant="caption" color="textSecondary">
                      {t('back')}
                    </Typography>
                  </CustomButtonLink>
                </Grid>
              </Grid>
            </Drawer>
          </HiddenDesktop>
        )}
      </Box>
    </HiddenDesktop>
  )
}

export default AdminMobileMenu
